import React, { useState, useEffect } from "react";
import { Pivot, PivotItem, Spinner, SpinnerSize, Text, IconButton, MessageBar, MessageBarType, Dropdown, IDropdownOption, PrimaryButton, DefaultButton } from "@fluentui/react";
import styles from "./Oportunidades.module.css";
import { capitalizeFirstLetter } from "../../utils/textUtils";
import { savedCarsService } from "../../api/savedCarsService";

interface Deal {
    id_unico: string;
    marca: string;
    modelo: string;
    version?: string;
    fecha_fabricacion: number;
    transparencia: number;
    precio: number;
    precio_mercado_promedio?: number;
    precio_mercado_minimo?: number;
    precio_mercado_maximo?: number;
    departamento?: string;
    kilometraje?: number;
    transmision?: string;
    tipo_combustible?: string;
    categoria?: string;
    imagen_url?: string;
    comentario_experto?: string;
    vendedor_nombre?: string;
    vendedor_telefono?: string;
    fecha_publicacion?: string;
    url?: string;
    financiamiento?: string;
    mantenimiento?: string;
    cambio?: string;
    negociable?: string;
    unico_dueno?: string;
    color?: string;
    acepta_cambios?: string;
    mantenimiento_detalle?: string;
    financiamiento_detalle?: string;
}

interface OportunidadesProps {
    onClose?: () => void;
    onSelectCar: (car: Deal) => void;
}

export const Oportunidades: React.FC<OportunidadesProps> = ({ onClose, onSelectCar }) => {
    const [deals, setDeals] = useState<Record<string, Deal[]>>({});
    const [selectedCategory, setSelectedCategory] = useState<string>("todos");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [savedCarMessage, setSavedCarMessage] = useState<string | null>(null);

    // Filter state
    const [departamentos, setDepartamentos] = useState<string[]>([]);
    const [marcas, setMarcas] = useState<string[]>([]);
    const [modelos, setModelos] = useState<string[]>([]);
    const [tiposVendedor, setTiposVendedor] = useState<string[]>([]);

    const [selectedDepartamento, setSelectedDepartamento] = useState<string | null>(null);
    const [selectedMarca, setSelectedMarca] = useState<string | null>(null);
    const [selectedModelo, setSelectedModelo] = useState<string | null>(null);
    const [selectedTipoVendedor, setSelectedTipoVendedor] = useState<string | null>(null);

    const [appliedDepartamento, setAppliedDepartamento] = useState<string | null>(null);
    const [appliedMarca, setAppliedMarca] = useState<string | null>(null);
    const [appliedModelo, setAppliedModelo] = useState<string | null>(null);
    const [appliedTipoVendedor, setAppliedTipoVendedor] = useState<string | null>(null);

    const [showFilters, setShowFilters] = useState<boolean>(false);

    const categories = [
        { key: "todos", label: "Todos" },
        { key: "sedan", label: "Sedan" },
        { key: "suv", label: "SUV" },
        { key: "pickup", label: "Pickup" },
        { key: "hatchback", label: "Hatchback" },
    ];

    // Debug logging on every render
    useEffect(() => {
        console.log("Oportunidades Component State:", {
            isLoading,
            error,
            dealsKeys: Object.keys(deals),
            dealsCount: Object.values(deals).reduce((sum, arr) => sum + arr.length, 0),
            selectedCategory,
            currentCategoryDeals: deals[selectedCategory]?.length || 0
        });
    });

    useEffect(() => {
        loadDeals();
        loadFilterOptions();
    }, []);

    const loadFilterOptions = async () => {
        try {
            // Load departamentos
            const deptResponse = await fetch("/api/deals/departamentos");
            if (deptResponse.ok) {
                const data = await deptResponse.json();
                if (data.success && data.departamentos) {
                    setDepartamentos(data.departamentos);
                }
            }

            // Load marcas
            const marcasResponse = await fetch("/api/deals/marcas");
            if (marcasResponse.ok) {
                const data = await marcasResponse.json();
                if (data.success && data.marcas) {
                    setMarcas(data.marcas);
                }
            }

            // Load modelos
            const modelosResponse = await fetch("/api/deals/modelos");
            if (modelosResponse.ok) {
                const data = await modelosResponse.json();
                if (data.success && data.modelos) {
                    setModelos(data.modelos);
                }
            }

            // Load tipos vendedor
            const tiposResponse = await fetch("/api/deals/tipos-vendedor");
            if (tiposResponse.ok) {
                const data = await tiposResponse.json();
                if (data.success && data.tipos_vendedor) {
                    setTiposVendedor(data.tipos_vendedor);
                }
            }
        } catch (err) {
            console.error("Error loading filter options:", err);
        }
    };

    const loadDeals = async (
        departamentoFilter?: string | null,
        marcaFilter?: string | null,
        modeloFilter?: string | null,
        tipoVendedorFilter?: string | null
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            // Build API URL with filters
            let apiUrl = "/api/deals";
            const params = new URLSearchParams();

            if (departamentoFilter) {
                params.append("departamento", departamentoFilter);
            }
            if (marcaFilter) {
                params.append("marca", marcaFilter);
            }
            if (modeloFilter) {
                params.append("modelo", modeloFilter);
            }
            if (tipoVendedorFilter) {
                params.append("tipo_vendedor", tipoVendedorFilter);
            }

            if (params.toString()) {
                apiUrl += `?${params.toString()}`;
            }

            console.log("Fetching deals from:", apiUrl);
            console.log("Current location:", window.location.href);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin'
            });

            console.log("Response status:", response.status);
            console.log("Response headers:", response.headers);
            const contentType = response.headers.get("content-type");
            console.log("Content-Type:", contentType);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("API Error Response:", errorText);
                throw new Error(`Failed to fetch deals: ${response.status}`);
            }

            // Check if response is JSON
            if (contentType && contentType.indexOf("application/json") !== -1) {
                const data = await response.json();
                console.log("API Response data:", data);

                if (data.success && data.deals_by_category) {
                    console.log("Setting deals by category:", data.deals_by_category);
                    setDeals(data.deals_by_category);
                } else if (data.deals) {
                    // Single category response
                    console.log("Setting single category deals:", data.category, data.deals);
                    setDeals({ [data.category]: data.deals });
                } else {
                    console.warn("Unexpected API response format:", data);
                    setError("Formato de respuesta inesperado del servidor.");
                }
            } else {
                // Response is not JSON (likely HTML)
                const text = await response.text();
                console.error("Received non-JSON response:", text.substring(0, 200));
                throw new Error("La API devolvió HTML en lugar de JSON. Verifica que el servidor backend esté ejecutándose en el puerto 8000.");
            }
        } catch (err) {
            console.error("Error fetching deals:", err);
            setError(`Error al cargar las oportunidades: ${err instanceof Error ? err.message : 'Error desconocido'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return `$${Math.round(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    };

    const formatMileage = (mileage?: number) => {
        if (!mileage) return "";
        return `${mileage.toLocaleString()} km`;
    };

    const getPriceStatus = (deal: Deal) => {
        // If no market data available, show "No disponible"
        if (!deal.precio_mercado_promedio || deal.precio_mercado_promedio === 0) {
            return { status: 'unavailable', label: 'No disponible', icon: '❓', color: '#6b7280' };
        }

        // If missing maximo, also show "No disponible"
        if (!deal.precio_mercado_maximo || deal.precio_mercado_maximo === 0) {
            return { status: 'unavailable', label: 'No disponible', icon: '❓', color: '#6b7280' };
        }

        // precio <= promedio: "Buen Precio" with green checkmark
        if (deal.precio <= deal.precio_mercado_promedio) {
            return { status: 'good', label: 'Buen Precio', icon: '✓', color: '#22c55e' };
        }

        // precio > promedio but <= maximo: "Precio Justo" with orange checkmark
        if (deal.precio > deal.precio_mercado_promedio && deal.precio <= deal.precio_mercado_maximo) {
            return { status: 'fair', label: 'Precio Justo', icon: '✓', color: '#ffa500' };
        }

        // precio > maximo: "Revisa Precio" with orange exclamation mark
        return { status: 'high', label: 'Revisa Precio', icon: '⚠', color: '#ffa500' };
    };

    const getTransparencyBadgeClass = (score: number) => {
        if (score >= 90) return styles.transparencyExcellent;
        if (score >= 80) return styles.transparencyGood;
        if (score >= 70) return styles.transparencyFair;
        return styles.transparencyLow;
    };

    const handleApplyFilters = () => {
        setAppliedDepartamento(selectedDepartamento);
        setAppliedMarca(selectedMarca);
        setAppliedModelo(selectedModelo);
        setAppliedTipoVendedor(selectedTipoVendedor);
        loadDeals(selectedDepartamento, selectedMarca, selectedModelo, selectedTipoVendedor);
    };

    const handleClearFilters = () => {
        setSelectedDepartamento(null);
        setSelectedMarca(null);
        setSelectedModelo(null);
        setSelectedTipoVendedor(null);
        setAppliedDepartamento(null);
        setAppliedMarca(null);
        setAppliedModelo(null);
        setAppliedTipoVendedor(null);
        loadDeals(null, null, null, null);
    };

    const handleSaveCar = async (car: Deal, e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            // Check if car is already saved in the database
            const isAlreadySaved = await savedCarsService.isCarSaved(car.id_unico);

            if (!isAlreadySaved) {
                // Save to database
                await savedCarsService.saveCar(car.id_unico);

                // Dispatch custom event to update saved cars count
                window.dispatchEvent(new Event('savedCarsUpdated'));

                // Show success message
                setSavedCarMessage(`${capitalizeFirstLetter(car.marca)} ${capitalizeFirstLetter(car.modelo)} guardado exitosamente`);
            } else {
                // Show already saved message
                setSavedCarMessage('Este auto ya está guardado');
            }

            // Auto-hide message after 3 seconds
            setTimeout(() => {
                setSavedCarMessage(null);
            }, 3000);
        } catch (error) {
            console.error("Error saving car:", error);
            setSavedCarMessage('Error al guardar el auto');
            setTimeout(() => {
                setSavedCarMessage(null);
            }, 3000);
        }
    };

    const renderDealCard = (deal: Deal, index: number) => (
        <div key={deal.id_unico} className={styles.dealCard} onClick={() => {
            onSelectCar(deal);
            onClose?.();
        }}>
            <div className={styles.rankingBadge} style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', background: 'rgba(0, 0, 0, 0.7)' }}>
                <span className={styles.rankingNumber} style={{ background: 'linear-gradient(135deg, #ffd700, #ffed4e)', backgroundColor: '#ffd700', color: '#333' }}>#{index + 1}</span>
            </div>
            <div className={styles.transparencyBadge} style={{ backgroundColor: 'rgb(0, 120, 212)', background: 'rgb(0, 120, 212)' }}>
                <span className={styles.transparencyScore} style={{ color: 'white' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={styles.shieldIcon}>
                        <path d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z" fill="white"/>
                    </svg>
                    {deal.transparencia}%
                </span>
            </div>

            {deal.imagen_url ? (
                <div className={styles.carImage}>
                    <img
                        src={deal.imagen_url}
                        alt={`${deal.marca} ${deal.modelo}`}
                        loading="lazy"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder-car.svg';
                        }}
                    />
                </div>
            ) : (
                <div className={styles.imagePlaceholder}>
                    <svg width="100" height="60" viewBox="0 0 100 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="100" height="60" fill="#E5E5E5"/>
                        <path d="M30 25C30 22.2386 32.2386 20 35 20H65C67.7614 20 70 22.2386 70 25V35H30V25Z" fill="#C4C4C4"/>
                        <path d="M25 35H75V40C75 42.7614 72.7614 45 70 45H30C27.2386 45 25 42.7614 25 40V35Z" fill="#C4C4C4"/>
                        <circle cx="35" cy="40" r="3" fill="#999"/>
                        <circle cx="65" cy="40" r="3" fill="#999"/>
                    </svg>
                    <Text className={styles.placeholderText}>[Imagen {deal.categoria}]</Text>
                </div>
            )}

            <div className={styles.dealContent}>
                <div className={styles.dealHeader}>
                    <Text className={styles.dealTitle}>
                        {capitalizeFirstLetter(deal.marca)} {capitalizeFirstLetter(deal.modelo)} {deal.fecha_fabricacion}
                    </Text>
                    <Text className={styles.dealSubtitle}>
                        {[
                            deal.version && capitalizeFirstLetter(deal.version),
                            deal.transmision && capitalizeFirstLetter(deal.transmision),
                            formatMileage(deal.kilometraje) || "Kilometraje no especificado",
                            deal.departamento && capitalizeFirstLetter(deal.departamento)
                        ].filter(Boolean).join(' • ')}
                    </Text>
                </div>

                <div className={styles.priceSection}>
                    <div className={styles.priceInfo}>
                        <Text className={styles.dealPrice}>{formatPrice(deal.precio)}</Text>
                        {deal.precio_mercado_promedio && deal.precio_mercado_promedio > 0 && (
                            <Text className={styles.priceDifference} style={{ color: getPriceStatus(deal).color }}>
                                {deal.precio <= deal.precio_mercado_promedio ? '-' : '+'}
                                {formatPrice(Math.abs(deal.precio - deal.precio_mercado_promedio))} vs mercado
                            </Text>
                        )}
                    </div>
                    <div className={styles.priceStatus}>
                        <div className={styles.priceStatusIconWrapper}>
                            {getPriceStatus(deal).status === 'unavailable' ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" fill={getPriceStatus(deal).color} />
                                    <line
                                        x1="8"
                                        y1="12"
                                        x2="16"
                                        y2="12"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            ) : getPriceStatus(deal).status === 'good' || getPriceStatus(deal).status === 'fair' ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" fill={getPriceStatus(deal).color} />
                                    <path
                                        d="M9 12l2 2 4-4"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" fill={getPriceStatus(deal).color} />
                                    <line
                                        x1="12"
                                        y1="8"
                                        x2="12"
                                        y2="12"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                    <circle cx="12" cy="16" r="1" fill="white" />
                                </svg>
                            )}
                        </div>
                        <span className={styles.priceStatusLabel} style={{ color: getPriceStatus(deal).color }}>
                            {getPriceStatus(deal).label}
                        </span>
                    </div>
                </div>

                <div className={styles.dealFeatures}>
                    {deal.mantenimiento && deal.mantenimiento !== "Sin especificar" && (
                        <span className={styles.feature}>
                            <span className={styles.featureIcon}>✓</span>
                            <span className={styles.featureLabel}>Mantenimiento:</span>
                            <span className={styles.featureValue}>{capitalizeFirstLetter(deal.mantenimiento)}</span>
                        </span>
                    )}
                    {deal.unico_dueno === "Si" && (
                        <span className={styles.feature}>
                            <span className={styles.featureIcon}>✓</span>
                            <span className={styles.featureText}>Único dueño</span>
                        </span>
                    )}
                    {deal.negociable === "Si" && (
                        <span className={styles.feature}>
                            <span className={styles.featureIcon}>✓</span>
                            <span className={styles.featureText}>Negociable</span>
                        </span>
                    )}
                    {deal.financiamiento && deal.financiamiento !== "SIN ESPECIFICAR" && (
                        <span className={styles.feature}>
                            <span className={styles.featureIcon}>✓</span>
                            <span className={styles.featureText}>Financiamiento</span>
                        </span>
                    )}
                </div>

                <div className={styles.dealActions}>
                    <button
                        className={styles.viewDetailsButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectCar(deal);
                            onClose?.();
                        }}
                    >
                        Ver detalles
                    </button>
                    <button
                        className={styles.saveButton}
                        onClick={(e) => handleSaveCar(deal, e)}
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.oportunidadesContainer}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.titleSection}>
                        <span className={styles.fireIcon}>🔥</span>
                        <div>
                            <Text className={styles.title}>Oportunidades de la Semana</Text>
                        </div>
                    </div>
                    {onClose && (
                        <IconButton
                            iconProps={{ iconName: "Cancel" }}
                            onClick={onClose}
                            className={styles.closeButton}
                            ariaLabel="Cerrar"
                        />
                    )}
                </div>
            </div>

            <MessageBar
                messageBarType={MessageBarType.info}
                className={styles.disclaimer}
                isMultiline={true}
            >
                Identificamos las publicaciones más confiables usando reputación del vendedor, calidad de la publicación y documentación del cuidado del vehículo.
            </MessageBar>

            {savedCarMessage && (
                <MessageBar
                    messageBarType={MessageBarType.success}
                    className={styles.successMessage}
                    onDismiss={() => setSavedCarMessage(null)}
                >
                    {savedCarMessage}
                </MessageBar>
            )}

            {/* Filters Section */}
            <div className={styles.filtersSection}>
                <div className={styles.filtersHeader}>
                    <button
                        className={styles.toggleFiltersButton}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={styles.filterIcon}>
                            <path d="M3 4h18M3 12h12M3 20h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span>Filtros</span>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                        >
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                    {(appliedDepartamento || appliedMarca || appliedModelo || appliedTipoVendedor) && (
                        <div className={styles.activeFiltersIndicator}>
                            {appliedDepartamento && (
                                <span className={styles.filterBadge}>
                                    📍 {capitalizeFirstLetter(appliedDepartamento)}
                                    <button
                                        className={styles.removeBadge}
                                        onClick={() => {
                                            setSelectedDepartamento(null);
                                            setAppliedDepartamento(null);
                                            loadDeals(null, appliedMarca, appliedModelo, appliedTipoVendedor);
                                        }}
                                        aria-label="Limpiar filtro departamento"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {appliedMarca && (
                                <span className={styles.filterBadge}>
                                    🚗 {capitalizeFirstLetter(appliedMarca)}
                                    <button
                                        className={styles.removeBadge}
                                        onClick={() => {
                                            setSelectedMarca(null);
                                            setAppliedMarca(null);
                                            loadDeals(appliedDepartamento, null, appliedModelo, appliedTipoVendedor);
                                        }}
                                        aria-label="Limpiar filtro marca"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {appliedModelo && (
                                <span className={styles.filterBadge}>
                                    🔧 {capitalizeFirstLetter(appliedModelo)}
                                    <button
                                        className={styles.removeBadge}
                                        onClick={() => {
                                            setSelectedModelo(null);
                                            setAppliedModelo(null);
                                            loadDeals(appliedDepartamento, appliedMarca, null, appliedTipoVendedor);
                                        }}
                                        aria-label="Limpiar filtro modelo"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {appliedTipoVendedor && (
                                <span className={styles.filterBadge}>
                                    👤 {capitalizeFirstLetter(appliedTipoVendedor)}
                                    <button
                                        className={styles.removeBadge}
                                        onClick={() => {
                                            setSelectedTipoVendedor(null);
                                            setAppliedTipoVendedor(null);
                                            loadDeals(appliedDepartamento, appliedMarca, appliedModelo, null);
                                        }}
                                        aria-label="Limpiar filtro tipo vendedor"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {showFilters && (
                    <div className={styles.filtersContent}>
                        <div className={styles.filterRow}>
                            <Dropdown
                                placeholder="Seleccionar departamento"
                                label="Departamento"
                                options={[
                                    { key: 'all', text: 'Todos los departamentos' },
                                    ...departamentos.map(dept => ({
                                        key: dept,
                                        text: capitalizeFirstLetter(dept)
                                    }))
                                ]}
                                selectedKey={selectedDepartamento || 'all'}
                                onChange={(_, option) => {
                                    if (option) {
                                        setSelectedDepartamento(option.key === 'all' ? null : option.key as string);
                                    }
                                }}
                                className={styles.filterDropdown}
                                styles={{
                                    dropdown: { width: '100%' },
                                    title: {
                                        background: 'white',
                                        borderColor: '#e8e3f0',
                                        borderWidth: '2px'
                                    },
                                    dropdownItemSelected: {
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))'
                                    }
                                }}
                            />
                            <Dropdown
                                placeholder="Seleccionar marca"
                                label="Marca"
                                options={[
                                    { key: 'all', text: 'Todas las marcas' },
                                    ...marcas.map(marca => ({
                                        key: marca,
                                        text: capitalizeFirstLetter(marca)
                                    }))
                                ]}
                                selectedKey={selectedMarca || 'all'}
                                onChange={(_, option) => {
                                    if (option) {
                                        setSelectedMarca(option.key === 'all' ? null : option.key as string);
                                    }
                                }}
                                className={styles.filterDropdown}
                                styles={{
                                    dropdown: { width: '100%' },
                                    title: {
                                        background: 'white',
                                        borderColor: '#e8e3f0',
                                        borderWidth: '2px'
                                    },
                                    dropdownItemSelected: {
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))'
                                    }
                                }}
                            />
                            <Dropdown
                                placeholder="Seleccionar modelo"
                                label="Modelo"
                                options={[
                                    { key: 'all', text: 'Todos los modelos' },
                                    ...modelos.map(modelo => ({
                                        key: modelo,
                                        text: capitalizeFirstLetter(modelo)
                                    }))
                                ]}
                                selectedKey={selectedModelo || 'all'}
                                onChange={(_, option) => {
                                    if (option) {
                                        setSelectedModelo(option.key === 'all' ? null : option.key as string);
                                    }
                                }}
                                className={styles.filterDropdown}
                                styles={{
                                    dropdown: { width: '100%' },
                                    title: {
                                        background: 'white',
                                        borderColor: '#e8e3f0',
                                        borderWidth: '2px'
                                    },
                                    dropdownItemSelected: {
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))'
                                    }
                                }}
                            />
                            <Dropdown
                                placeholder="Seleccionar tipo de vendedor"
                                label="Tipo de Vendedor"
                                options={[
                                    { key: 'all', text: 'Todos los tipos' },
                                    ...tiposVendedor.map(tipo => ({
                                        key: tipo,
                                        text: capitalizeFirstLetter(tipo)
                                    }))
                                ]}
                                selectedKey={selectedTipoVendedor || 'all'}
                                onChange={(_, option) => {
                                    if (option) {
                                        setSelectedTipoVendedor(option.key === 'all' ? null : option.key as string);
                                    }
                                }}
                                className={styles.filterDropdown}
                                styles={{
                                    dropdown: { width: '100%' },
                                    title: {
                                        background: 'white',
                                        borderColor: '#e8e3f0',
                                        borderWidth: '2px'
                                    },
                                    dropdownItemSelected: {
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))'
                                    }
                                }}
                            />
                        </div>
                        <div className={styles.filterActions}>
                            <PrimaryButton
                                text="Aplicar Filtros"
                                onClick={handleApplyFilters}
                                className={styles.applyButton}
                                styles={{
                                    root: {
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 24px'
                                    },
                                    rootHovered: {
                                        background: 'linear-gradient(135deg, #5568d3, #653a8b)',
                                    }
                                }}
                            />
                            {(selectedDepartamento || selectedMarca || selectedModelo || selectedTipoVendedor ||
                              appliedDepartamento || appliedMarca || appliedModelo || appliedTipoVendedor) && (
                                <DefaultButton
                                    text="Limpiar Todos"
                                    onClick={handleClearFilters}
                                    className={styles.clearButton}
                                    styles={{
                                        root: {
                                            borderColor: '#e8e3f0',
                                            borderWidth: '2px',
                                            borderRadius: '8px',
                                            padding: '10px 24px'
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <Spinner size={SpinnerSize.large} label="Cargando oportunidades..." />
                </div>
            ) : error ? (
                <MessageBar messageBarType={MessageBarType.error} className={styles.errorMessage}>
                    {error}
                    <br/>
                    <button onClick={() => loadDeals()}>Reintentar</button>
                </MessageBar>
            ) : (
                <>
                    <Pivot
                        className={styles.categoryTabs}
                        selectedKey={selectedCategory}
                        onLinkClick={(item) => item && setSelectedCategory(item.props.itemKey!)}
                        overflowBehavior="menu"
                    >
                        {categories.map(category => (
                            <PivotItem
                                key={category.key}
                                itemKey={category.key}
                                headerText={category.label}
                                className={styles.pivotItem}
                            />
                        ))}
                    </Pivot>

                    <div className={styles.dealsGrid}>
                        {deals[selectedCategory]?.length > 0 ? (
                            deals[selectedCategory].slice(0, 5).map((deal, index) => renderDealCard(deal, index))
                        ) : (
                            <div className={styles.emptyState}>
                                <Text className={styles.emptyText}>
                                    No hay oportunidades disponibles en esta categoría
                                </Text>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Legal Disclaimer */}
            <div className={styles.legalDisclaimer}>
                <div className={styles.disclaimerIcon}>ℹ️</div>
                <Text className={styles.disclaimerText}>
                    <strong>Información importante:</strong> La puntuación de transparencia y el precio de mercado son guías orientativas basadas en datos disponibles.
                    No constituyen una tasación profesional ni una recomendación oficial de compra.
                    Videntis no tiene ninguna afiliación con estos negocios ni recibe ningún tipo de pagos por anuncios o comisiones por ventas.
                    Es responsabilidad del comprador realizar las debidas diligencias, incluyendo inspección mecánica,
                    verificación de documentos y prueba de manejo antes de concretar cualquier transacción.
                    Videntis no se hace responsable por la exactitud de la información proporcionada por terceros.
                </Text>
            </div>
        </div>
    );
};